import React, {useEffect, useState} from 'react';
import {Dimensions, ScrollView, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {makeStyles} from '@rn-vui/themed';

import Button from '../..//components/themed/Button';
import Text from '../../components/themed/Text';
import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import CrossPlatformModal from '../../components/ui-controls/CrossPlatformModal';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Markdown from '../../components/ui-controls/Markdown';
import PressableWithOpacityFeedback from '../../components/ui-controls/PressableWithOpacityFeedback';
import XIcon from '../../images/brand/svg/icons/X.svg';
import {AirtableInformation} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {InformationContentType, RouteNavigatorScreenProps} from '../../models/navigationTypes';
import apiService from '../../transport/apiService';

const RewardsTermsAndConditions = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.REWARDS_TERMS_AND_CONDITIONS>) => {
  const [isLoading, setIsLoading] = useState(true);
  const [information, setInformation] = useState<AirtableInformation>();
  const contentType: InformationContentType = InformationContentType.TERMS_AND_CONDITIONS;

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      const info = (await apiService.getInformation('RewardsProgramTermsAndConditions')) || [];
      setInformation(info.data);
      setIsLoading(false);
    }
    initialise();
  }, [contentType]);

  if (isLoading) {
    return (
      <ContentView testID="rewards-terms-and-conditions-screen-loading">
        <ActivityIndicator />
      </ContentView>
    );
  }

  return (
    <KeyboardAwareScrollView
      style={{flex: 1}}
      contentContainerStyle={{flex: 1}}
      keyboardShouldPersistTaps="always">
      <ContentView
        parentStyle={{flex: 1, paddingBottom: 16}}
        style={{flex: 1}}
        testID="rewards-terms-and-conditions-screen">
        {information ? <Markdown>{information?.content}</Markdown> : null}
        <View style={{flexGrow: 1}} />
        <View style={{paddingVertical: 16}}>
          <Button
            secondary
            title="CLOSE"
            onPress={() => {
              onAccept?.();
              navigation.goBack();
            }}
          />
        </View>
      </ContentView>
    </KeyboardAwareScrollView>
  );
};

function dashCaseToTitleCase(str: string) {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

const RewardsTermsAndConditionsDialog = ({
  isVisible,
  onClose,
  contentType = InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
}: {
  isVisible: boolean;
  onClose: any;
  contentType?: InformationContentType;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [information, setInformation] = useState<AirtableInformation>();

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      const info = (await apiService.getInformation(dashCaseToTitleCase(contentType))) || [];
      setInformation(info.data);
      setIsLoading(false);
    }

    initialise();
  }, [contentType]);

  const insets = useSafeAreaInsets();

  return (
    <CrossPlatformModal
      isVisible={isVisible}
      hasBackdrop
      backdropOpacity={0.75}
      onBackdropPress={onClose}
      onBackButtonPress={onClose}
      style={{padding: 0, margin: 0}}>
      <View
        style={{
          maxHeight: Dimensions.get('window').height,
          backgroundColor: 'white',
          paddingTop: insets.top,
          paddingBottom: insets.bottom,
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 20,
          }}>
          <Text style={{marginTop: 24, marginBottom: 18}} variant="heading1">
            {contentType === InformationContentType.PRIVACY_POLICY
              ? 'PRIVACY POLICY'
              : 'TERMS & CONDITIONS'}
          </Text>
          <PressableWithOpacityFeedback onPress={onClose}>
            <XIcon />
          </PressableWithOpacityFeedback>
        </View>
        <ScrollView
          style={{
            paddingTop: 10,
            paddingHorizontal: 20,
          }}>
          <>
            {isLoading ? (
              <ActivityIndicator />
            ) : information ? (
              <Markdown>{information?.content}</Markdown>
            ) : null}
          </>
          <View style={{paddingVertical: 20}}>
            <Button variant="outlined" onPress={onClose}>
              CLOSE
            </Button>
          </View>
        </ScrollView>
      </View>
    </CrossPlatformModal>
  );
};

/**
 * Easily popup information content. e.g. for Terms and Conditions or Privacy Policy.
 */
function RewardsTermsAndConditionsPopper({
  contentType = InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
  text = `Terms and Conditions`,
}: {
  contentType?: InformationContentType;
  text?: string;
}) {
  const [showTerms, setShowTerms] = React.useState(false);
  const styles = useStyles();
  return (
    <>
      <Text variant="body2" style={styles.link} onPress={() => setShowTerms(true)}>
        {text || `Terms and Conditions`}
      </Text>
      <RewardsTermsAndConditionsDialog
        contentType={contentType}
        isVisible={showTerms}
        onClose={() => setShowTerms(false)}
      />
    </>
  );
}

/**
 * This is used as alternative to the RewardsTermsAndConditionsPopper if need to use it
 * as child of Text node
 */
export function useRewardsTermsAndConditionsPopper({
  contentType = InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
  text = `Terms and Conditions`,
}: {
  contentType?: InformationContentType;
  text?: string;
}) {
  const [showTerms, setShowTerms] = React.useState(false);
  const styles = useStyles();

  const textNode = (
    <Text variant="body2" style={styles.link} onPress={() => setShowTerms(true)}>
      {text || `Terms and Conditions`}
    </Text>
  );

  const dialogNode = (
    <RewardsTermsAndConditionsDialog
      contentType={contentType}
      isVisible={showTerms}
      onClose={() => setShowTerms(false)}
    />
  );

  return {
    textNode,
    dialogNode,
  };
}

export function useAirtableInformation(contentType: InformationContentType) {
  const [isLoading, setIsLoading] = useState(true);
  const [information, setInformation] = useState<AirtableInformation>();

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      const info = (await apiService.getInformation(dashCaseToTitleCase(contentType))) || [];
      setInformation(info.data);
      setIsLoading(false);
    }
    initialise();
  }, [contentType]);

  return {isLoading, information};
}

export default RewardsTermsAndConditions;

export {RewardsTermsAndConditionsDialog, RewardsTermsAndConditionsPopper};

const useStyles = makeStyles(theme => ({
  link: {
    color: theme.colors.link || '#1C64F2',
  },
}));
